import React, { createContext, useContext, useReducer, ReactNode } from 'react'
import {
  DashboardBuilderState,
  DashboardBuilderContextType,
  WidgetType,
  ComponentVariant,
  DashboardTemplate,
} from '@otw/models'

const initialState: DashboardBuilderState = {
  isBuilderMode: false,
  selectedWidgets: [],
  hoveredWidget: null,
  componentVariants: {} as Record<WidgetType, ComponentVariant>,
  editingTemplate: null,
}

type DashboardBuilderAction =
  | { type: 'TOGGLE_BUILDER_MODE' }
  | { type: 'SELECT_WIDGET'; payload: { widgetId: WidgetType; componentVariant?: ComponentVariant } }
  | { type: 'DESELECT_WIDGET'; payload: WidgetType }
  | { type: 'SET_HOVERED_WIDGET'; payload: WidgetType | null }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'START_EDITING_TEMPLATE'; payload: DashboardTemplate }
  | { type: 'CLEAR_EDITING_TEMPLATE' }

const loadTemplateSelection = (template: DashboardTemplate) => {
  const selectedWidgets = template.widgets.map((widget) => widget.id)
  const componentVariants = {} as Record<WidgetType, ComponentVariant>
  for (const widget of template.widgets) {
    if (widget.componentVariant) {
      componentVariants[widget.id] = widget.componentVariant
    }
  }
  return { selectedWidgets, componentVariants }
}

const dashboardBuilderReducer = (
  state: DashboardBuilderState,
  action: DashboardBuilderAction
): DashboardBuilderState => {
  switch (action.type) {
    case 'TOGGLE_BUILDER_MODE':
      return {
        ...state,
        isBuilderMode: !state.isBuilderMode,
        selectedWidgets: [],
        hoveredWidget: null,
        componentVariants: {} as Record<WidgetType, ComponentVariant>,
        editingTemplate: null,
      }

    case 'SELECT_WIDGET':
      return {
        ...state,
        selectedWidgets: state.selectedWidgets.includes(action.payload.widgetId)
          ? state.selectedWidgets
          : [...state.selectedWidgets, action.payload.widgetId],
        componentVariants: action.payload.componentVariant
          ? { ...state.componentVariants, [action.payload.widgetId]: action.payload.componentVariant }
          : state.componentVariants,
      }

    case 'DESELECT_WIDGET': {
      const nextVariants = { ...state.componentVariants }
      delete nextVariants[action.payload]
      return {
        ...state,
        selectedWidgets: state.selectedWidgets.filter((widget) => widget !== action.payload),
        componentVariants: nextVariants,
      }
    }

    case 'SET_HOVERED_WIDGET':
      return {
        ...state,
        hoveredWidget: action.payload,
      }

    case 'CLEAR_SELECTION':
      return {
        ...state,
        selectedWidgets: [],
        hoveredWidget: null,
        componentVariants: {} as Record<WidgetType, ComponentVariant>,
      }

    case 'START_EDITING_TEMPLATE': {
      const { selectedWidgets, componentVariants } = loadTemplateSelection(action.payload)
      return {
        ...state,
        isBuilderMode: true,
        selectedWidgets,
        componentVariants,
        hoveredWidget: null,
        editingTemplate: action.payload,
      }
    }

    case 'CLEAR_EDITING_TEMPLATE':
      return {
        ...state,
        editingTemplate: null,
        selectedWidgets: [],
        hoveredWidget: null,
        componentVariants: {} as Record<WidgetType, ComponentVariant>,
      }

    default:
      return state
  }
}

const DashboardBuilderContext = createContext<DashboardBuilderContextType | undefined>(undefined)

interface DashboardBuilderProviderProps {
  children: ReactNode
  previewMode?: boolean
}

export const DashboardBuilderProvider: React.FC<DashboardBuilderProviderProps> = ({
  children,
  previewMode = false,
}) => {
  const [state, dispatch] = useReducer(dashboardBuilderReducer, initialState)

  const effectiveState = {
    ...state,
    isBuilderMode: previewMode ? false : state.isBuilderMode,
  }

  const toggleBuilderMode = () => {
    dispatch({ type: 'TOGGLE_BUILDER_MODE' })
  }

  const selectWidget = (widgetId: WidgetType, componentVariant?: ComponentVariant) => {
    dispatch({ type: 'SELECT_WIDGET', payload: { widgetId, componentVariant } })
  }

  const deselectWidget = (widgetId: WidgetType) => {
    dispatch({ type: 'DESELECT_WIDGET', payload: widgetId })
  }

  const setHoveredWidget = (widgetId: WidgetType | null) => {
    dispatch({ type: 'SET_HOVERED_WIDGET', payload: widgetId })
  }

  const clearSelection = () => {
    dispatch({ type: 'CLEAR_SELECTION' })
  }

  const startEditingTemplate = (template: DashboardTemplate) => {
    dispatch({ type: 'START_EDITING_TEMPLATE', payload: template })
  }

  const clearEditingTemplate = () => {
    dispatch({ type: 'CLEAR_EDITING_TEMPLATE' })
  }

  const contextValue: DashboardBuilderContextType = {
    state: effectiveState,
    toggleBuilderMode,
    selectWidget,
    deselectWidget,
    setHoveredWidget,
    clearSelection,
    startEditingTemplate,
    clearEditingTemplate,
  }

  return (
    <DashboardBuilderContext.Provider value={contextValue}>
      {children}
    </DashboardBuilderContext.Provider>
  )
}

export const useDashboardBuilder = (): DashboardBuilderContextType => {
  const context = useContext(DashboardBuilderContext)
  if (context === undefined) {
    throw new Error('useDashboardBuilder must be used within a DashboardBuilderProvider')
  }
  return context
}

/** True when the current selection differs from the template being edited (or has any widgets in create mode). */
export const isDashboardBuilderSelectionDirty = (state: DashboardBuilderState): boolean => {
  const { editingTemplate, selectedWidgets, componentVariants } = state

  if (!editingTemplate) {
    return selectedWidgets.length > 0
  }

  const baselineIds = editingTemplate.widgets.map((widget) => widget.id).slice().sort()
  const currentIds = selectedWidgets.slice().sort()

  if (baselineIds.length !== currentIds.length) return true
  if (baselineIds.some((id, index) => id !== currentIds[index])) return true

  for (const widget of editingTemplate.widgets) {
    const currentVariant = componentVariants[widget.id]
    if (widget.componentVariant) {
      if (currentVariant !== widget.componentVariant) return true
    } else if (currentVariant) {
      return true
    }
  }

  return false
}
